import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CommonService } from 'src/app/shared/service/common.service';
import { AuthenticationService } from 'src/app/shared/service/authentication.service';
import { SsoService } from '../services/sso.service';
import { REFRESH_TOKEN } from 'src/app/shared/api.constant';
import { selectSelectedCountry } from 'src/app/store/appContext/app-context.selector';
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly refreshTokenSubject = new BehaviorSubject<string | null>(
    null
  );
  private selectedCountry: any = null;

  constructor(
    private readonly commonService: CommonService,
    private readonly SsoService: SsoService,
    private readonly authenticationService: AuthenticationService,
    private readonly store: Store
  ) {
    this.store.select(selectSelectedCountry).subscribe((country) => {
      this.selectedCountry = country;
    });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const accessToken = this.commonService.getAccessToken();

    if (accessToken) {
      request = this.addToken(request, accessToken);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // Login API → do nothing
          if (this.isLoginApi(request.url)) {
            return throwError(() => error);
          }

          // Refresh token API → logout
          if (this.isRefreshApi(request.url)) {
            this.logout();
            return throwError(() => error);
          }

          // Other APIs → try refresh token
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  // ---------------- PRIVATE METHODS ----------------

  private handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const refreshToken = this.commonService.getRefreshToken();

      if (!refreshToken) {
        this.logout();
        return throwError(() => 'No refresh token available');
      }

      return this.refreshToken(refreshToken).pipe(
        switchMap((res: any) => {
          this.isRefreshing = false;

          const newAccessToken = res?.data?.response?.accessToken;
          this.commonService.setAccessToken(newAccessToken);
          this.refreshTokenSubject.next(newAccessToken);

          return next.handle(this.addToken(request, newAccessToken));
        }),
        catchError((err) => {
          this.isRefreshing = false;
          this.logout();
          return throwError(() => err);
        })
      );
    }

    // Queue pending requests
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addToken(request, token || '')))
    );
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    const country = this.selectedCountry;
    if (
      token &&
      !request.url.includes('googleapis') &&
      !request.url.includes('my.salesforce.com')
    ) {
      let headers = request.headers.set('Authorization', `${token}`);

      if (country) {
        headers = headers.set('x-region-timezone', country.timezoneCode ?? '');
      }

      const isFormData = request.body instanceof FormData;

      if (isFormData) {
        const skipContentType = ['/upload'];
        const shouldSkipContentType = skipContentType.some((url) =>
          request.url.includes(url)
        );

        if (shouldSkipContentType) {
          headers = headers.delete('Content-Type');
        }
      } else {
        headers = headers.set('Content-Type', 'application/json');
      }

      return request.clone({ headers });
    }

    return request;
  }

  refreshToken(refreshToken: string) {
    return this.SsoService.get(REFRESH_TOKEN, { token: refreshToken });
  }

  private logout(): void {
    this.authenticationService.logoutWithoutApi();
  }

  private isLoginApi(url: string): boolean {
    return url.includes('login');
  }

  private isRefreshApi(url: string): boolean {
    return url.includes(REFRESH_TOKEN);
  }
}
